import { ACCOUNT_TYPE, selectUserData } from 'containers/Account/accountSlice'
import { selectBasket, updateTerms } from 'containers/OrderDetails/basketSlice'
import useUserFetch from 'containers/Account/hooks/useUserFetch'
import { useReduxDispatch, useReduxSelector } from 'store'
import React, { useEffect, useRef, useState } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import { useHistory } from 'react-router-dom'
import { displayPrice } from 'utils/string'
import Loading from 'components/Loading'
import { toLongDate } from 'utils/date'
import './style.scss'

const type = ACCOUNT_TYPE.basic

const OrderContract: React.FC = () => {
  const basket = useReduxSelector(selectBasket)
  const [isSignButton, showSignButton] = useState(false)
  const status = useUserFetch(type) as response.StatusTypes
  const { data: userData } = useReduxSelector(state => selectUserData(state, type))
  const { t, lang } = useTranslation('contract')
  const isSigning = basket.terms.rentalTerms

  const mainRef = useRef<HTMLDivElement>(null)
  const companyName = getCompanyName()
  const date = toLongDate(new Date(), lang, true)
  const { firstWarehouse, locks } = (basket.list as any[])?.reduce((r, item) => {
    if (!r.firstWarehouse && item.type === 'rent') r.firstWarehouse = item
    else if (item.type === 'rentAddon') r.locks += (r.locks ? ', ' : '') + item.name
    return r
  }, {
    firstWarehouse: undefined as any,
    locks: ''
  })

  const dispatch = useReduxDispatch()
  const { push, replace } = useHistory()

  useEffect(() => {
    if (!Array.isArray(basket.list) || basket.list.length === 0) {
      replace('/map')
    }
  }, [basket.list, replace])

  useEffect(() => {
    const main = mainRef.current
    if (status === 'succeed' && main && main.scrollHeight === main.clientHeight) {
      showSignButton(true)
    }
  }, [status])

  return (
    <div className='orderContract'>
      <div className='title'>
        <div>{t('rentalAgreementForWarehouse')}</div>
        <div>{t('concludedOn')} <strong>{date}</strong>, {t('between')}:</div>
      </div>

      {status === 'succeed'
        ? <div
          ref={mainRef}
          className='orderContract__main'
          onScroll={e => {
            const main = mainRef.current
            if (!main) return
            if ((main.clientHeight + main.scrollTop + 1) >= main.scrollHeight) {
              showSignButton(true)
            } else if (isSignButton) {
              showSignButton(false)
            }
          }}>
          <div className='orderContract__section'>
            <div className='orderContract__section__block'>
              <h2>{t('lessor')}</h2>
              {basket.invoice
                ? <ul>
                  <li>Nazwa: <b>{basket.invoice?.name}</b></li>
                  <li>Adres: <b>{basket.invoice?.address}</b></li>
                  <li>NIP: <b>{basket.invoice?.nip}</b></li>
                  <li>Reprezentowany przez: <b>{userData?.first_name} {userData?.surname}</b></li>
                </ul>
                : <ul>
                  <li>Imię i nazwisko: <b>{userData?.first_name} {userData?.surname}</b></li>
                  <li>Adres: <b>{userData?.street} {userData?.building_nr}{userData?.apartment_nr ? `/${userData.apartment_nr}` : ''}, {userData?.zip_code} {userData?.city}</b></li>
                  <li>PESEL: <b>{userData?.pesel}</b></li>
                </ul>}
            </div>

            <div className='orderContract__section__block'>
              <h2>{t('lessee')}</h2>
              <ul>
                <li>Nazwa: <b>{companyName}</b></li>
                <li>Adres: <b>Al. Tadeusza Kościuszki 101, 90-441 Łódź</b></li>
                <li>NIP: <b>XXXXXXXXXXX</b></li>
              </ul>
            </div>
          </div>

          <div className='orderContract__section'>
            <h2>§1. Przedmiot umowy</h2>
            <ul>
              <li>Wynajmujący oddaje Najemcy do używania <strong>jeden lub więcej magazynów self-storage</strong>, wskazanych poniżej. Każdy z magazynów (dalej: „Magazyny”) opisany jest przez numer, lokalizację oraz powierzchnię.
                <ol>
                  {basket.list && (basket.list as any[]).map(item => {
                    if (item.type !== 'rent') return null
                    return <li key={item.identifier}>
                      <strong>{item.name}</strong>, położony w <strong>{item.address?.street} {item.address?.estateNumber}{item.address?.localNumber ? `/${item.address.localNumber}` : ''}, {item.address?.city}</strong>, o powierzchni <strong>{item?.size?.area} m²</strong>
                    </li>
                  })}
                </ol>
              </li>
              <li>Każdy Magazyn przeznaczony jest wyłącznie do przechowywania rzeczy ruchomych.</li>
              <li>Zabrania się przechowywania w Magazynach:
                <ul>
                  <li>materiałów łatwopalnych, toksycznych, wybuchowych lub niebezpiecznych,</li>
                  <li>żywych zwierząt,</li>
                  <li>żywności łatwo psującej się,</li>
                  <li>rzeczy nielegalnych lub pochodzących z przestępstwa.</li>
                </ul>
              </li>
            </ul>
          </div>

          <div className='orderContract__section'>
            <h2>§2. Czas trwania umowy</h2>
            <ol>
              <li>Umowa zostaje zawarta na czas <strong>{firstWarehouse?.rangeDate?.m === -1 ? 'nieokreślony' : 'określony'}</strong>.</li>
              <li>W przypadku umowy na czas nieokreślony Strony ustalają okres wypowiedzenia: <strong>{firstWarehouse?.termiateDays}</strong>.</li>
            </ol>
          </div>

          <div className='orderContract__section'>
            <h2>§3. Czynsz i opłaty</h2>
            <ol>
              <li>Najemca zobowiązuje się do płacenia czynszu w wysokości:
                {basket?.rentingSummary?.to_pay?.monthly && <ul>
                  {(basket.rentingSummary.to_pay.monthly as any[]).map((item, idx) => <li key={idx}>
                    {item.title} <b>{displayPrice(item.price_sum)}</b>
                  </li>)}
                </ul>}
              </li>
              <li>Czynsz płatny jest z góry do dnia <strong>18</strong> każdego miesiąca na rachunek Wynajmującego.</li>
              <li>Za opóźnienie w płatności naliczane będą odsetki ustawowe.</li>
              <li>Wynajmujący może zmienić wysokość czynszu z jednomiesięcznym wyprzedzeniem, informując Najemcę na piśmie lub e-mailem.</li>
            </ol>
          </div>

          <div className='orderContract__section'>
            <h2>§4. Dostęp do Magazynu</h2>
            <ol>
              <li>Najemca otrzymuje dostęp do Magazynu poprzez: <strong>{locks}</strong>.</li>
              <li>Najemca jest odpowiedzialny za zabezpieczenie Magazynu przed osobami nieuprawnionymi.</li>
              <li>W przypadku utraty klucza lub karty dostępu Najemca ponosi koszt wymiany w wysokości <strong>100 zł</strong>.</li>
            </ol>
          </div>

          <div className='orderContract__section'>
            <h2>§5. Obowiązki Najemcy</h2>
            <ol>
              <li>Utrzymania porządku i bezpieczeństwa w Magazynie.</li>
              <li>Nieuszkadzania elementów konstrukcyjnych, instalacji oraz wyposażenia.</li>
              <li>Zgłaszania usterek niezwłocznie po ich stwierdzeniu.</li>
              <li>Użytkowania Magazynu zgodnie z jego przeznaczeniem.</li>
              <li>Nieprowadzenia działalności gospodarczej w Magazynie, w tym nieprzebywania tam na stałe.</li>
            </ol>
          </div>

          <div className='orderContract__section'>
            <h2>§6. Odpowiedzialność i ubezpieczenie</h2>
            <ol>
              <li>Wynajmujący nie odpowiada za rzeczy przechowywane przez Najemcę, chyba że szkoda powstała z winy Wynajmującego.</li>
              <li>Najemca może (opcjonalnie) wykupić ubezpieczenie przechowywanego mienia.</li>
              <li>Najemca ponosi odpowiedzialność za szkody powstałe z jego winy lub winy osób, którym umożliwił dostęp.</li>
            </ol>
          </div>

          <div className='orderContract__section'>
            <h2>§7. Rozwiązanie umowy</h2>
            <ol>
              <li>Umowa może zostać rozwiązana:
                <ul>
                  <li>za porozumieniem Stron,</li>
                  <li>przez wypowiedzenie zgodnie z §2,</li>
                  <li>ze skutkiem natychmiastowym w przypadku rażącego naruszenia umowy przez Najemcę.</li>
                </ul>
              </li>
              <li>Po zakończeniu umowy Najemca zobowiązuje się opróżnić Magazyn i zwrócić go w stanie niepogorszonym.</li>
            </ol>
          </div>

          <div className='orderContract__section'>
            <h2>§8. Dane osobowe</h2>
            <p>Strony oświadczają, że dane osobowe są przetwarzane zgodnie z RODO i wyłącznie na potrzeby realizacji niniejszej umowy.</p>
          </div>

          <div className='orderContract__section'>
            <h2>§9. Postanowienia końcowe</h2>
            <ol>
              <li>W sprawach nieuregulowanych zastosowanie mają przepisy Kodeksu cywilnego.</li>
              <li>Umowę sporządzono w dwóch jednobrzmiących egzemplarzach, po jednym dla każdej ze Stron.</li>
              <li>Załączniki stanowią integralną część umowy.</li>
            </ol>

            <div className='orderContract__sign'>
              <div className='orderContract__sign__box'>
                <strong>{t('date')}:</strong>
                <div className='orderContract__sign__signature__wrapper'>
                  <div
                    className={`orderContract__sign__signature${isSigning ? ' --animate' : ''}`}
                    style={{
                      '--chars': date.length
                    } as React.CSSProperties}>
                    {date}
                  </div>
                </div>
                <div className='orderContract__sign__line' />
              </div>
              <div className='orderContract__sign__box'>
                <strong>{t('lessee')}:</strong>
                <div className='orderContract__sign__signature__wrapper'>
                  <div
                    className={`orderContract__sign__signature${isSigning ? ' --animate' : ''}`}
                    style={{
                      '--chars': `${userData?.first_name} ${userData?.surname}`.length
                    } as React.CSSProperties}>
                    {userData?.first_name} {userData?.surname}
                  </div>
                  {!isSigning && <div
                    className='orderContract__sign__signature--button'
                    onClick={() => {
                      dispatch(updateTerms({ ...basket.terms, rentalTerms: true }))
                    }}>
                    {t('sign')}
                  </div>}
                </div>
                <div className='orderContract__sign__line' />
              </div>
            </div>
          </div>
        </div>
        : <div className='orderContract__main --basic'>
          <Loading />
        </div>}

      <ArrowBtn
        enlarged
        disabled={status !== 'succeed' || (isSignButton && !isSigning)}
        text={isSignButton
          ? t('continue')
          : t('swipeToContinue')}
        action={() => {
          if (isSignButton && isSigning) {
            push('/order/payment')
          } else {
            const main = mainRef.current
            if (!main) return
            const maxTop = main.scrollHeight - main.clientHeight + 1
            const potentialNextTop = main.scrollTop + main.clientHeight
            const nextTop = Math.min(maxTop, potentialNextTop)
            main.scrollTo({ top: nextTop, behavior: 'smooth' })
          }
        }}
      />
    </div>
  )
}

const getCompanyName = () => {
  switch (process.env.REACT_APP_TYPE) {
    case 'rze01':
      return 'A&P GROUP sp. z o.o.'
    case 'mm':
      return 'Magazynomaty sp. z o.o.'
    default:
      return 'Automatyczne magazyny sp. z o.o.'
  }
}

export default OrderContract